import React from "react";
import { useTranslation } from "react-i18next";

import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import { FormikProps } from "formik";

import { FormValues } from "../../schemas";
import { FREQ_LABELS } from "../constants";
import { WithdrawalValues } from "../types";
import { SummaryRow, SummarySection } from "../SummaryRow";

interface Step9Props {
  formik: FormikProps<FormValues>;
  dateFormat: string;
  currency: string;
  withdrawalValues: WithdrawalValues;
  serverError: string[] | null;
}

export const Step9DrawdownReview: React.FC<Step9Props> = ({
  formik, dateFormat, currency, withdrawalValues: wv, serverError
}) => {
  const { t } = useTranslation("debt");
  const { values } = formik;

  const fmtDate = (d: string | null | undefined) =>
    d ? dayjs(d).format(dateFormat || "YYYY-MM-DD") : "—";
  const fmtFreq = (f: string | null | undefined) => (f ? FREQ_LABELS[f] ?? f : null);

  return (
    <Box>
      <SummarySection title={t("wizard.drawdownReview.details")}>
        {wv.deal_title && <SummaryRow label={t("wizard.common.name")} value={wv.deal_title} />}
        <SummaryRow label={t("wizard.common.amount")} value={`${currency} ${wv.loan_amount?.toLocaleString()}`} />
        <SummaryRow label={t("wizard.drawdownReview.dateLabel")} value={fmtDate(wv.start_date)} />
      </SummarySection>

      {wv.loan_first_status_date && (
        <SummarySection title={t("wizard.drawdownReview.statusReporting")}>
          <SummaryRow label={t("wizard.drawdownReview.firstStatusDate")} value={fmtDate(wv.loan_first_status_date)} />
          <SummaryRow label={t("wizard.common.frequency")} value={fmtFreq(values.loan_status_frequency)} />
        </SummarySection>
      )}

      {(wv.principal_first_payment_date || wv.interest_first_payment_date || wv.pik_first_capitalization_date) && (
        <SummarySection title={t("wizard.drawdownReview.firstPaymentDates")}>
          {wv.principal_first_payment_date && (
            <SummaryRow label={t("wizard.drawdownReview.firstPrincipal")} value={fmtDate(wv.principal_first_payment_date)} />
          )}
          {wv.interest_first_payment_date && (
            <SummaryRow label={t("wizard.drawdownReview.firstInterest")} value={fmtDate(wv.interest_first_payment_date)} />
          )}
          {wv.pik_first_capitalization_date && (
            <SummaryRow label={t("wizard.drawdownReview.firstPIK")} value={fmtDate(wv.pik_first_capitalization_date)} />
          )}
        </SummarySection>
      )}

      <Box sx={{ mt: 3, p: 3, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "action.hover" }}>
        <Typography variant="body2" color="text.secondary">
          {t("wizard.drawdownReview.inheritedNote")}
        </Typography>
      </Box>

      {serverError && (
        <Box sx={{ mt: 3, p: 3, borderRadius: 2, border: "1px solid", borderColor: "error.main", bgcolor: "error.50" }}>
          <Typography variant="body2" color="error.main" fontWeight={600} gutterBottom>
            {t("wizard.common.validationError")}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {serverError.map((msg, i) => (
              <Typography key={i} component="li" variant="body2" color="error.dark" sx={{ wordBreak: "break-word" }}>
                {msg}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
